import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { mockStats, mockProducts, mockCaseStudies } from "../mock";
import { ArrowRight, Users, TrendingUp, FileText, HelpCircle, Calculator, CheckCircle, Cog } from "lucide-react";

const HomePage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section - Corporate industrial style */}
      <section className="relative bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="inline-block px-4 py-2 bg-excel-light-red text-white text-sm font-semibold rounded">
                  SPÉCIALISTE DEPUIS 1991
                </div>
                <h1 className="text-4xl md:text-6xl font-bold text-excel-dark leading-tight font-montserrat">
                  Solutions de levage industriel sur mesure
                </h1>
                <p className="text-xl text-gray-700 leading-relaxed font-medium">
                  Expertise, fiabilité et sécurité pour tous vos besoins de manutention.
                </p>
                {/* Normes CSA/ASME et Bot assistance supprimés */}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <Button 
                  asChild 
                  size="lg"
                  className="bg-excel-red hover:bg-excel-dark text-white px-10 py-4 text-lg font-semibold shadow-lg"
                >
                  <Link to="/contact">
                    Demander un devis
                  </Link>
                </Button>
                <Button 
                  asChild 
                  variant="outline"
                  size="lg"
                  className="border-2 border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white px-10 py-4 text-lg font-semibold"
                >
                  <Link to="/products">
                    Découvrir nos solutions
                  </Link>
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-excel-red/10 to-excel-steel/10 rounded-xl -rotate-3"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_excel-revamp-quebec/artifacts/xuelyiil_1-Usine%20Industrielle%20Excel%20Levage%20Sans%20Travailleurs.png"
                alt="Excel Levage Industrial Overhead Crane"
                className="relative w-full h-96 object-cover rounded-xl shadow-2xl"
              />
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-4">
                <div className="text-excel-dark font-semibold">Pont roulant industriel</div>
                <div className="text-excel-steel text-sm">Solution robuste et personnalisée</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Excellence industrielle Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-excel-dark mb-4 font-montserrat">
              Excellence industrielle depuis 1991
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Excel Levage s'impose comme un leader dans la conception et la fabrication d'équipements de levage industriel au Québec.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-excel-light-red w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-excel-dark mb-2">Sécurité garantie</h3>
              <p className="text-gray-600">Conformité stricte aux normes CSA et ASME pour tous nos équipements</p>
            </div>

            <div className="text-center">
              <div className="bg-excel-light-red w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Cog className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-excel-dark mb-2">Solutions robustes</h3>
              <p className="text-gray-600">Équipements conçus pour résister aux environnements industriels les plus exigeants</p>
            </div>

            <div className="text-center">
              <div className="bg-excel-light-red w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-excel-dark mb-2">Performance optimale</h3>
              <p className="text-gray-600">Solutions personnalisées pour maximiser votre productivité et efficacité</p>
            </div>

            <div className="text-center">
              <div className="bg-excel-light-red w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-excel-dark mb-2">Service expert</h3>
              <p className="text-gray-600">Accompagnement complet de la conception à la maintenance par nos spécialistes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Services Section - Exact content structure */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-excel-dark mb-2 font-montserrat">
              Nos solutions de levage
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Ponts roulants */}
            <div className="text-center space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <img
                  src="https://customer-assets.emergentagent.com/job_excel-revamp-quebec/artifacts/joww2j8m_2-%20Pont%20Roulant%20Excel%20Levage%20Corrig%C3%A9.png"
                  alt="Ponts roulants"
                  className="w-full h-24 object-cover rounded mb-4"
                />
                <h3 className="font-bold text-excel-dark text-lg mb-2">Ponts roulants</h3>
              </div>
              <p className="text-sm text-gray-600">
                Ponts roulants robustes et personnalisés pour tous vos besoins de levage industriel.
              </p>
              <Link to="/products" className="text-excel-light-red text-sm font-medium hover:underline">
                En savoir plus
              </Link>
            </div>

            {/* Palans et treuils */}
            <div className="text-center space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <img
                  src="https://customer-assets.emergentagent.com/job_excel-revamp-quebec/artifacts/o3u6zw5q_3-%20Palan%20EXL%20Op%C3%A9rateur%20Sans%20Fond.png"
                  alt="Palans et treuils"
                  className="w-full h-24 object-cover rounded mb-4"
                />
                <h3 className="font-bold text-excel-dark text-lg mb-2">Palans et treuils</h3>
              </div>
              <p className="text-sm text-gray-600">
                Palans et treuils motorisés pour une manipulation précise et sécuritaire.
              </p>
              <Link to="/products" className="text-excel-light-red text-sm font-medium hover:underline">
                En savoir plus
              </Link>
            </div>

            {/* Systèmes de manutention */}
            <div className="text-center space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <img
                  src="https://customer-assets.emergentagent.com/job_excel-revamp-quebec/artifacts/fw5v6ucm_4-%20Station%20Manutention%20EXCEL%20LEVAGE.png"
                  alt="Systèmes de manutention"
                  className="w-full h-24 object-cover rounded mb-4"
                />
                <h3 className="font-bold text-excel-dark text-lg mb-2">Systèmes de manutention</h3>
              </div>
              <p className="text-sm text-gray-600">
                Systèmes de manutention spécialisés adaptés à vos environnements industriels.
              </p>
              <Link to="/products" className="text-excel-light-red text-sm font-medium hover:underline">
                En savoir plus
              </Link>
            </div>

            {/* Services de maintenance */}
            <div className="text-center space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <img
                  src="https://customer-assets.emergentagent.com/job_excel-revamp-quebec/artifacts/1s7fmvuv_6-%20Maintenance%20Pont%20Roulant.png"
                  alt="Services de maintenance"
                  className="w-full h-24 object-cover rounded mb-4"
                />
                <h3 className="font-bold text-excel-dark text-lg mb-2">Services de maintenance</h3>
              </div>
              <p className="text-sm text-gray-600">
                Services de maintenance et d'inspection pour garantir la sécurité de vos équipements.
              </p>
              <Link to="/products" className="text-excel-light-red text-sm font-medium hover:underline">
                En savoir plus
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Chiffres clés corporate */}
      <section className="py-16 bg-excel-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">
              Performance et fiabilité prouvées
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-8">
              <div className="text-5xl font-bold text-excel-red mb-3">
                {mockStats.yearsExperience}+
              </div>
              <div className="text-white text-lg font-medium">Années d'expérience</div>
              <div className="text-gray-300 text-sm mt-2">Excellence industrielle depuis 1991</div>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-8">
              <div className="text-5xl font-bold text-excel-red mb-3">
                {mockStats.projectsDelivered}+
              </div>
              <div className="text-white text-lg font-medium">Projets livrés</div>
              <div className="text-gray-300 text-sm mt-2">Solutions réalisées avec succès</div>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-8">
              <div className="text-5xl font-bold text-excel-red mb-3">
                {mockStats.branches}
              </div>
              <div className="text-white text-lg font-medium">Succursales à travers le Québec</div>
              <div className="text-gray-300 text-sm mt-2">Présence régionale pour mieux vous servir</div>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Preview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-excel-dark mb-4 font-montserrat">
              Ressources et outils
            </h2>
            <p className="text-lg text-gray-600">
              Guides pratiques, FAQ et outils techniques pour vous accompagner
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow border-l-4 border-l-excel-light-red">
              <CardHeader>
                <div className="bg-excel-light-red w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-excel-dark">Guides pratiques</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Documentation technique et guides de sélection pour vos équipements de levage
                </p>
                <Button asChild variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white">
                  <Link to="/resources">Consulter les guides</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow border-l-4 border-l-excel-light-red">
              <CardHeader>
                <div className="bg-excel-light-red w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <HelpCircle className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-excel-dark">FAQ technique</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Réponses aux questions les plus fréquentes sur nos équipements et services
                </p>
                <Button asChild variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white">
                  <Link to="/resources">Voir la FAQ</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow border-l-4 border-l-excel-light-red">
              <CardHeader>
                <div className="bg-excel-light-red w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Calculator className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-excel-dark">Outils de calcul</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Calculateurs en ligne pour dimensionner vos équipements de levage
                </p>
                <Button asChild variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white">
                  <Link to="/resources">Utiliser les outils</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-excel-steel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4 font-montserrat">
            Besoin d'une solution sur mesure ?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Contactez nos experts pour une consultation personnalisée et un devis technique détaillé.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg"
              className="bg-excel-red hover:bg-excel-dark text-white px-8 py-3"
            >
              <Link to="/contact">
                Demander un devis technique
              </Link>
            </Button>
            {/* Bouton boutique masqué temporairement
            <Button 
              asChild 
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-excel-steel px-8 py-3"
            >
              <Link to="/shop">
                Visiter notre boutique
              </Link>
            </Button>
            */}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-excel-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Prêt à démarrer votre projet ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Contactez nos experts pour une consultation personnalisée et un devis adapté à vos besoins.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg"
              className="bg-excel-light-red hover:bg-excel-red text-white"
            >
              <Link to="/contact">
                {t("getQuote")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              asChild 
              variant="outline" 
              size="lg"
              className="border-white text-white hover:bg-white hover:text-excel-dark"
            >
              <Link to="/about">En savoir plus</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;